import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  OnInit,
  ChangeDetectionStrategy,
  OnChanges, OnDestroy
} from '@angular/core';

import { Subject } from 'rxjs/Subject';

import {Translate, Utils} from '../../services';
import {Store} from "@ngrx/store";
import {ViewActions, ViewSelectors} from "../../logics/view";
import {Observable} from "rxjs";

@Component({
  selector: 'cs-ext-audio-player',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="cs-ext-audio-player-wrapper">
      <audio 
        preload="true"
        (timeupdate)="audioTimeupdate($event)"
        (ended)="audioEnded($event)"
        (loadedmetadata)="audioMetadata($event)"
        (durationchange)="audioDurationChange($event)"
        (progress)="audioProgress($event)"
        [volume]="currentVolume"
        #player
        [id]="id">
        <source 
          [src]="inputs.src">
      </audio>
      
      <div class="audio-interface-button first">
        <i
          *ngIf="isFullyLoaded"
          class="toggle-play cs-btn cs-btn-cta"
          [ngClass]="{
          'ion-play': !(playInProgress$ | async),
          'ion-stop': (playInProgress$ | async),
          'disabled': (playInProgress$ | async)
        }"
          (click)="toggle(inputs.confirmToPlay)">
        </i>
        <button
          class="cs-btn cs-btn-cta"
          *ngIf="!isFullyLoaded"
          >
          <div class="cs-loader">
            <div class="bounce1"></div>
            <div class="bounce2"></div>
          </div>
        </button>
      </div>
      <div class="audio-interface-button audio-toggler-wrapper" [ngClass]="{'open': controlAudioOpen}">
        <i class="audio-icon cs-btn cs-btn-cta" 
           [ngClass]="getVolumeIconClass()"
           (click)="toggleAudioControl($event)"></i>
        <div class="volume-slider-wrapper">
          <ng5-slider [(value)]="currentVolume" [options]="playerOptions" (userChange)="onUserChange($event)"></ng5-slider>
        </div>
      </div>
      <span class="current-time">
        {{currentTime}}
      </span>
      <div class="progress-bar-wrapper">
        <div class="buffer-bar" [ngStyle]="getBufferbarStyle()"></div>
        <div class="progress-bar" [ngStyle]="getProgressbarStyle()"></div>
      </div>
      <span class="total-time">{{totalTime}}</span>
    </div>
  `
})

export class CsExtAudioPlayerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() inputs: any = {};
  @Output() play = new EventEmitter<Object>();
  @Output() stop = new EventEmitter<Object>();
  @ViewChild('player') player: ElementRef;

  playInProgress$: Subject<boolean> = new Subject<boolean>();
  destroyed$: Subject<any> = new Subject<any>();
  isAudioExtPlayingAccepted$: Observable<any>;

  translations$: Observable<any>;
  translations: Object = {};

  id;
  src;
  interval;

  currentTime: string = '00:00';
  totalTime: string = '00:00';
  currentProgress: number = 0;
  currentBufferPerc: number = 0;
  currentVolume: number = 0.75;
  controlAudioOpen: boolean = false;
  isPlaying: boolean = false;
  isFullyLoaded: boolean = false;

  playerOptions = {
    floor: 0,
    ceil: 1,
    step: 0.01,
    showSelectionBar: true,
    animate: false,
    translate: (value, label): string => {
      return '';
    }
  };

  constructor(
    private store: Store<any>,
    private viewActions: ViewActions,
    private viewSelectors: ViewSelectors,
    private translate: Translate,
    private utils: Utils
  ) {
    this.id = this.utils.generateShortUID();
    this.playInProgress$.next(false);
    this.isAudioExtPlayingAccepted$ = this.viewSelectors.isAudioExtPlayingAccepted();
    this.translations$ = this.translate.getTranslations$();
  }

  ngOnInit() {
    this.src = this.inputs.src;
    this.player.nativeElement.volume = 75 / 100;
    this.currentVolume = this.player.nativeElement.volume;
    this.isAudioExtPlayingAccepted$
      .takeUntil(this.destroyed$)
      .subscribe(isAccepted => {
        if (isAccepted) {
          this.toggle(false);
          this.store.dispatch(this.viewActions.closeModal());
        }
      });
    this.translations$
      .takeUntil(this.destroyed$)
      .subscribe(translations => this.translations = translations);
  }

  toggle(confirm?) {

    if (!this.player.nativeElement.paused) {
      return;
    }

    if (confirm) {
      this.store.dispatch(this.viewActions.audioExtResetPlayAccepted());
      this.store.dispatch(this.viewActions.openModal({
        title: this.translations['modal#confirm_audio_start_title'],
        submitText: this.translations['modal#confirm'],
        description: this.translations['modal#confirm_audio_start_description'],
        isWarning: true,
        preventClose: false,
        action: this.viewActions.audioExtPlayAccepted()
      }));
      return;
    }

    if (this.player.nativeElement.paused) {
      this.play.emit({
        audio: this.player.nativeElement,
        id: this.id
      });

      this.store.dispatch(this.viewActions.audioExtStarted());
      this.playInProgress$.next(true);

      this.interval = setInterval(() => {
        if (this.player.nativeElement.paused) {
          clearInterval(this.interval);
          this.playInProgress$.next(false);
          this.store.dispatch(this.viewActions.audioExtEnded());
        }
      }, 500);
    }
  }

  getVolumeIconClass() {

    if (this.currentVolume === 0) {
      return 'ion-android-volume-mute';
    }

    if (this.currentVolume * 100 <= 33) {
      return 'ion-volume-low';
    }

    if (this.currentVolume * 100 <= 66) {
      return 'ion-volume-medium';
    }

    return 'ion-volume-high';
  }

  formatTime(time) {
    let timeFix = Math.ceil(time),
      parts = []
    ;
    parts[0] = Math.floor(timeFix / 60);
    parts[0] = parts[0] < 10 ? '0' + parts[0] : parts[0];
    parts[1] = timeFix % 60;
    parts[1] = parts[1] < 10 ? '0' + parts[1] : parts[1];
    return parts.join(':');
  }

  getProgressbarStyle() {
    let style = {
      //'background-image': 'linear-gradient(90deg, ' + this.inputs.sectionColor + ' ' + this.currentProgress + '%, transparent ' + this.currentProgress + '%)'
      'background-color': this.inputs.sectionColor,
      'width': this.currentProgress + '%'
    };

    return style;
  }

  getBufferbarStyle() {
    let style = {
      'width': this.currentBufferPerc + '%'
    };

    return style;
  }

  toggleAudioControl($event) {
    this.controlAudioOpen = !this.controlAudioOpen;
  }

  onUserChange($event) {

  }

  audioTimeupdate(event) {
    this.currentTime = this.formatTime(this.player.nativeElement.currentTime);
    this.currentProgress = (100 * this.player.nativeElement.currentTime / this.player.nativeElement.duration);
  }

  audioDurationChange(event) {
    this.totalTime = this.formatTime(this.player.nativeElement.duration);
  }

  audioProgress(event) {
    let perc = 0;
    if (this.player.nativeElement.seekable.length) {
      perc = 100 * this.player.nativeElement.seekable.end(0) / this.player.nativeElement.duration;
      this.currentBufferPerc = perc;
    }

    if (perc >= 99) {
      this.isFullyLoaded = true;
    }
  }

  audioMetadata(event) {
    this.audioProgress(event);
  }

  audioEnded(event) {
    this.playInProgress$.next(false);
    this.store.dispatch(this.viewActions.audioExtEnded());
  }

  ngOnChanges() {
    if (!this.src) {
      return;
    }

    if (this.src !== this.inputs.src) {
      this.currentTime = '00:00';
      this.totalTime = '00:00';
      this.currentProgress = 0;
      this.currentBufferPerc = 0;
      this.controlAudioOpen = false;
      this.isPlaying = false;
      this.isFullyLoaded = false;
      this.src = this.inputs.src;
      this.player.nativeElement.src = this.inputs.src;
    }
  }

  ngOnDestroy () {
    this.destroyed$.next();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/cs-ext-audio-player/cs-ext-audio-player.component.ts